import { ExternalLinkIcon } from 'lucide-react'
import { Fragment, useEffect, useId, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { asText } from '@/lib/audition/answer-shape'
import {
  otherAnswerName,
  settleOtherAnswers,
} from '@/lib/audition/other-answer'
import type {
  AuditionAnswer,
  AuditionArtifact,
  AuditionField,
  AuditionQuestionStep,
  StepArtifacts,
} from '@/lib/audition/steps'
import { artifactsSchema, stepSchema } from '@/lib/audition/steps'
import { WIDGETS } from '@/lib/audition/widgets'
import { NewTabLink } from './new-tab-link'
import { WIDGET_VIEWS } from './widgets'

/**
 * The answers a widget asked at this position may read: the fields asked
 * before it, and nothing else.
 *
 * The ONE producer of that rule, called by both halves — settling below and
 * drawing further down. It was written twice at first, and the two copies
 * already disagreed about a field that is missing from the bag: one wrote the
 * widget's empty value under the key, the other left the key out. Nothing
 * reaches that state today, which is exactly why it would have gone on
 * disagreeing until the seventh widget found it.
 *
 * Read straight off the bag the pass is building, so a widget sees an earlier
 * field as whatever that field will actually be drawn as — not as the value
 * settling computed for it and then discarded for being JSON-equal to what
 * was already stored.
 *
 * "Before" spans the whole wizard, not just this step: the level ladder is
 * asked on Goals and takes its rungs from `currentLevel`, which is asked on
 * Background. `before` carries the finished steps; this step's own earlier
 * fields are layered on top, so a name asked twice reads as the copy on the
 * page the applicant is actually looking at.
 */
function answersBefore(
  step: AuditionQuestionStep,
  at: number,
  answers: Record<string, AuditionAnswer | undefined>,
  before: Record<string, AuditionAnswer | undefined>,
): Record<string, AuditionAnswer | undefined> {
  const earlier: Record<string, AuditionAnswer | undefined> = { ...before }
  for (const field of step.fields.slice(0, at))
    earlier[field.name] = answers[field.name]
  return earlier
}

/**
 * Bring every widget's stored answer back into agreement with what it will
 * actually draw, by running each widget's `settle` down the step.
 *
 * Run on the seeded state and after every edit, because both can break the
 * agreement. A resumed draft may hold moves in the pre-ladder vocabulary, and
 * changing the level answer can close a rung the applicant had already
 * picked. Left alone, the widget shows one thing and Next submits another.
 *
 * Short of erasing anything a widget cannot draw: that is kept, and the
 * widget's `ready`, asked in `unanswered` below, holds Next on it instead.
 * Which things those are is `WidgetDescriptor`'s to say, not this loop's.
 *
 * One owner for "these have to agree", rather than an effect per widget, and
 * every settle function is idempotent so re-settling costs nothing. Which
 * widgets have settling to do is the registry's business, not this file's:
 * a widget added later is settled here without this loop being touched.
 */
function settleWidgets(
  step: AuditionQuestionStep,
  answers: Record<string, AuditionAnswer>,
  today: Date,
  before: Record<string, AuditionAnswer | undefined>,
): Record<string, AuditionAnswer> {
  let next = answers
  for (const [at, field] of step.fields.entries()) {
    const was = next[field.name]
    const now = WIDGETS[field.type].settle(was, {
      field,
      // Only the fields asked before this one. Handing over the whole bag
      // would let a widget settle against a value this same pass is about to
      // replace; here that field is simply not in the bag and reads
      // `undefined`, the "not said yet" path every widget already handles.
      answers: answersBefore(step, at, next, before),
      today,
    })
    // Identity first, so the widgets with nothing to settle — every text and
    // chip field, on every keystroke — cost a comparison rather than two
    // serializations.
    if (now === was || JSON.stringify(now) === JSON.stringify(was)) continue
    next = { ...next, [field.name]: now }
  }
  // Last, over the settled chips: an Other box's text goes the moment its
  // chip is unpicked, by the same rule `stepSchema` applies on the server.
  return settleOtherAnswers(step, next)
}

/**
 * Each artifact box's sentence right now, by column — the refusal a press of
 * Next would give these values. Blank is never refused, so an empty object
 * means every box is fine.
 *
 * A plain function, not a hook, so an edit can ask it about the values it
 * just produced rather than the ones still on screen.
 */
function refusalsNow(
  step: AuditionQuestionStep,
  artifacts: StepArtifacts,
): Partial<Record<string, string>> {
  const errors: Partial<Record<string, string>> = {}
  const parsed = artifactsSchema(step).safeParse(artifacts)
  if (parsed.success) return errors
  for (const issue of parsed.error.issues) {
    const column = issue.path.at(0)
    if (typeof column === 'string') errors[column] ??= issue.message
  }
  return errors
}

/**
 * The refused columns with everything since fixed taken out — the rule
 * `ContactStep.stillOwed` states, for the same reason. The box's sentence is
 * a live region read out over whatever is being typed, so between presses of
 * Next it only ever comes down: clearing a refused link and starting to
 * retype it must not read the whole sentence out again on the first
 * character, which is exactly the moment the link is briefly not a profile.
 */
function stillRefused(
  step: AuditionQuestionStep,
  refused: ReadonlySet<string>,
  artifacts: StepArtifacts,
): ReadonlySet<string> {
  if (refused.size === 0) return refused
  const now = refusalsNow(step, artifacts)
  const kept = [...refused].filter((column) => now[column] !== undefined)
  return kept.length === refused.size ? refused : new Set(kept)
}

/**
 * One artifact box: a link stored in its own column rather than in the
 * answers. Drawn the way Contact draws LinkedIn — the label row IS the link,
 * and the box takes its name from `aria-label` so it is not announced with
 * the link's "(opens in a new tab)".
 *
 * Always optional, and says so on the label like any optional question. Its
 * refusal is its own sentence on the box itself, never the step's "complete
 * the required fields": the only thing wrong with it can be a link that is
 * not a profile, and the way out is to fix it or clear it.
 */
function ArtifactBox({
  artifact,
  value,
  error,
  refusals,
  disabled,
  inputRef,
  onChange,
}: {
  artifact: AuditionArtifact
  value: string
  /** Set only once Next has been refused over this box. */
  error: string | undefined
  /** How many times Next has been refused — see the sentence's `key`. */
  refusals: number
  disabled: boolean
  inputRef: React.Ref<HTMLInputElement>
  onChange: (value: string) => void
}) {
  const helpId = `${artifact.column}-help`
  const errorId = `${artifact.column}-error`
  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium leading-none">
        <NewTabLink
          href={artifact.href}
          className="inline-flex items-center gap-1.5 underline underline-offset-4"
        >
          {artifact.label}
          <ExternalLinkIcon
            aria-hidden="true"
            className="text-muted-foreground size-3.5"
          />
        </NewTabLink>
        <span className="ml-1 font-normal text-muted-foreground">
          (optional)
        </span>
      </p>
      <p id={helpId} className="text-sm text-muted-foreground">
        {artifact.help}
      </p>
      <Input
        ref={inputRef}
        id={artifact.column}
        aria-label={artifact.label}
        type="text"
        inputMode="url"
        autoComplete="off"
        placeholder={artifact.placeholder}
        value={value}
        disabled={disabled}
        maxLength={artifact.limit}
        aria-describedby={error ? `${helpId} ${errorId}` : helpId}
        aria-invalid={error ? true : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {error && (
        <p
          // A new live region per refused press. Enter in this box submits
          // the form with focus already here, so a second refusal moves
          // nothing and says the same words: without a remount it is silent.
          key={refusals}
          id={errorId}
          role="alert"
          className="text-sm text-destructive"
        >
          {error}
        </p>
      )}
    </div>
  )
}

export function QuestionStep({
  step,
  defaults,
  artifactDefaults,
  before,
  onSubmit,
  onBack,
  isSubmitting,
  today,
}: {
  step: AuditionQuestionStep
  /**
   * Whatever has been saved for this step, which is routinely not much: the
   * wizard hands over `{}` for a step nobody has reached, and a draft written
   * before a field was added to a step has no key for it. So `| undefined`,
   * for the reason `StepContext.answers` carries it — without
   * `noUncheckedIndexedAccess` a total `Record` types every read as present,
   * and the `?? empty(f)` below, which is the thing actually holding this
   * together, reads to the checker as a fallback on a value that cannot be
   * missing.
   */
  defaults: Record<string, AuditionAnswer | undefined>
  /**
   * Every answer from the steps already finished, flattened by field name.
   *
   * A widget may read what was asked before it and nothing else, and since
   * the level ladder moved to Goals "before it" crosses a step boundary.
   * Passed in rather than read from a store here, so this component stays
   * the pure function of its props that its tests rely on.
   */
  before: Record<string, AuditionAnswer | undefined>
  /**
   * What this step's artifact boxes open with, by column. Left out by a
   * step that declares none.
   */
  artifactDefaults?: StepArtifacts
  /**
   * The artifacts arrive beside the answers and never inside them: they are
   * stored in columns, and only ever hold the columns THIS step declares.
   */
  onSubmit: (
    answers: Record<string, AuditionAnswer>,
    artifacts: StepArtifacts,
  ) => void
  onBack: () => void
  isSubmitting: boolean
  /**
   * Injectable so a test asserts against a fixed line. Left out in the app,
   * where the line is always drawn from the month the applicant is in.
   */
  today?: Date
}) {
  // The one date `StepContext.today` promises every widget, read once.
  //
  // A useState initializer, not useMemo: this is a correctness invariant, and
  // useMemo is a cache React may drop and recompute. Dropped after a month
  // boundary it would redraw the line a month on, under a half-finished tap
  // the timeline holds as a position on the line it replaced.
  const [day] = useState(() => today ?? new Date())
  const [answers, setAnswers] = useState<Record<string, AuditionAnswer>>(() => {
    const seeded: Record<string, AuditionAnswer> = {}
    for (const f of step.fields) {
      seeded[f.name] = defaults[f.name] ?? WIDGETS[f.type].empty(f)
      // Seeded beside its chip, so a resumed draft opens with what they
      // typed still in the Other box.
      if (f.other) {
        const name = otherAnswerName(f)
        seeded[name] = defaults[name] ?? ''
      }
    }
    return settleWidgets(step, seeded, day, before)
  })
  const [artifacts, setArtifacts] = useState<StepArtifacts>(() => {
    const seeded: StepArtifacts = {}
    for (const artifact of step.artifacts ?? [])
      seeded[artifact.column] = artifactDefaults?.[artifact.column] ?? ''
    return seeded
  })
  // The only bit that is not derivable: how many times Next has been refused.
  // A count and not a flag, so pressing Next on a step that is still
  // incomplete moves focus again — a second press that silently did nothing
  // is indistinguishable from a broken button.
  const [refusals, setRefusals] = useState(0)
  const parsed = useMemo(
    () => stepSchema(step).safeParse(answers),
    [step, answers],
  )

  /**
   * Which questions are the reason, so each can say so on itself: an answer
   * its schema refuses, or one its widget is not ready to let through.
   *
   * `ready` is asked here, beside `stepSchema` and never inside it, for the
   * reason `WidgetDescriptor` gives.
   */
  const unanswered = useMemo(() => {
    const named = new Set<string>()
    for (const [at, field] of step.fields.entries()) {
      const widget = WIDGETS[field.type]
      const value = answers[field.name]
      const ready = widget.ready(value, {
        field,
        answers: answersBefore(step, at, answers, before),
        today: day,
      })
      if (!ready || !widget.storedSchema(field).safeParse(value).success)
        named.add(field.name)
    }
    return named
  }, [step, answers, before, day])
  // Recomputed from the answers every render, so the message takes itself down
  // the moment the last field is filled rather than sitting there red until
  // the next press.
  const incomplete = refusals > 0 && (!parsed.success || unanswered.size > 0)
  const parsedArtifacts = useMemo(
    () => artifactsSchema(step).safeParse(artifacts),
    [step, artifacts],
  )
  const artifactErrors = useMemo(
    () => refusalsNow(step, artifacts),
    [step, artifacts],
  )
  /**
   * The columns the last press of Next refused, pruned as each is fixed. A
   * box says nothing until it is in here, so its sentence appears on a press
   * and never mid-keystroke — see `stillRefused`.
   */
  const [refusedArtifacts, setRefusedArtifacts] = useState<ReadonlySet<string>>(
    () => new Set(),
  )

  const refusal = useRef<HTMLParagraphElement>(null)
  const refusedArtifact = useRef<HTMLInputElement>(null)
  const refusalId = useId()

  /**
   * What this question points `aria-describedby` at: its help text, and the
   * refusal when this is one of the questions being refused.
   *
   * The refusal is named here rather than left to `aria-invalid` because
   * `aria-invalid` is only honoured on input roles and `radiogroup` — four of
   * the six widgets are a `group` or a `list`, where it is simply ignored.
   * `aria-describedby` is global, so this is the one way to say "this
   * question is why" that works for every widget.
   */
  const describes = (field: AuditionField): string | undefined =>
    [
      field.help ? `${field.name}-help` : '',
      incomplete && unanswered.has(field.name) ? refusalId : '',
    ]
      .filter(Boolean)
      .join(' ') || undefined
  useEffect(() => {
    // Focus follows the refusal. The message can be a whole screen below the
    // button they pressed, and a screen-reader user who is not moved to it is
    // simply left where they were with nothing said about why Next did
    // nothing.
    //
    // When every question is answered and the only refusal is an artifact
    // link, there is no step-level message to move to, so focus goes to the
    // box itself, which its own sentence describes.
    if (refusals === 0) return
    if (refusal.current) refusal.current.focus()
    else refusedArtifact.current?.focus()
  }, [refusals])

  const set = (name: string, value: AuditionAnswer) =>
    setAnswers((a) => settleWidgets(step, { ...a, [name]: value }, day, before))

  /**
   * Values and refusal move together, as Contact's reducer keeps them: the
   * edit prunes the refusal against the values it produced, so no write can
   * leave a sentence standing over a box that now parses.
   */
  const editArtifact = (column: AuditionArtifact['column'], value: string) => {
    const next = { ...artifacts, [column]: value }
    setArtifacts(next)
    setRefusedArtifacts((refused) => stillRefused(step, refused, next))
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!parsed.success || unanswered.size > 0 || !parsedArtifacts.success) {
      setRefusals((n) => n + 1)
      setRefusedArtifacts(new Set(Object.keys(artifactErrors)))
      return
    }
    onSubmit(parsed.data, parsedArtifacts.data)
  }

  return (
    <form onSubmit={submit} className="space-y-6">
      <h1 className="text-2xl font-semibold">{step.title}</h1>
      {step.description && (
        <p className="text-muted-foreground">{step.description}</p>
      )}
      {step.fields.map((field, at) => {
        const { Component, labelledControl } = WIDGET_VIEWS[field.type]
        const heading = (
          <>
            {field.label}
            {!field.required && (
              <span className="ml-1 text-muted-foreground">(optional)</span>
            )}
          </>
        )
        const artifactsHere = (step.artifacts ?? []).filter(
          (artifact) => artifact.after === field.name,
        )
        return (
          <Fragment key={field.name}>
            <div className="space-y-1.5">
              {labelledControl ? (
                <Label htmlFor={field.name}>{heading}</Label>
              ) : (
                // A group has no single focusable control to point htmlFor at,
                // so its heading is plain text and the widget carries the
                // accessible name on the group itself.
                <p className="text-sm font-medium">{heading}</p>
              )}
              {field.help && (
                // Pointed at by the widget, not merely sitting next to it: help
                // text a screen reader is never told about is help nobody gets.
                <p
                  id={`${field.name}-help`}
                  className="text-sm text-muted-foreground"
                >
                  {field.help}
                </p>
              )}
              <Component
                field={field}
                value={answers[field.name]}
                answers={answersBefore(step, at, answers, before)}
                today={day}
                disabled={isSubmitting}
                describedBy={describes(field)}
                invalid={incomplete && unanswered.has(field.name)}
                onChange={(value) => set(field.name, value)}
                otherText={
                  field.other
                    ? {
                        value: asText(answers[otherAnswerName(field)]),
                        onChange: (text) => set(otherAnswerName(field), text),
                      }
                    : undefined
                }
              />
            </div>
            {artifactsHere.map((artifact) => {
              const error = refusedArtifacts.has(artifact.column)
                ? artifactErrors[artifact.column]
                : undefined
              return (
                <ArtifactBox
                  key={artifact.column}
                  artifact={artifact}
                  value={artifacts[artifact.column] ?? ''}
                  error={error}
                  refusals={refusals}
                  disabled={isSubmitting}
                  // Only a refused box takes the ref, so focus lands on the
                  // one that is actually the reason.
                  inputRef={error ? refusedArtifact : null}
                  onChange={(value) => editArtifact(artifact.column, value)}
                />
              )
            })}
          </Fragment>
        )
      })}
      {incomplete && (
        <p
          ref={refusal}
          id={refusalId}
          role="alert"
          // Focusable only by being sent here; it never joins the tab order.
          tabIndex={-1}
          className="text-sm text-destructive outline-none"
        >
          Please complete the required fields.
        </p>
      )}
      <div className="flex gap-3">
        {/* Disabled while a save is in flight: onSuccess advances by a
            RELATIVE increment, so navigating Back mid-save would get yanked
            forward off the step the user just moved to. */}
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          disabled={isSubmitting}
        >
          Back
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          Next
        </Button>
      </div>
    </form>
  )
}
